import { REPORT_STATUS_INITIAL } from "@/constants/qmaPipelineConstants";
import { getAllCompIdsFromAdditionalAttributes } from "@/utils/compIdsUtils";
import { hasCompsAnalysisContent } from "@/utils/compsAnalysisUtils";

/**
 * @param {unknown} value
 * @returns {string}
 */
function trimString(value) {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * True when the property has a persisted QMA artifact (report text, S3 key, or report URL).
 * Aligns with fields merged in PropertyDetailsStore and property detail modals.
 *
 * @param {object|null|undefined} property
 * @returns {boolean}
 */
export function propertyHasQmaReport(property) {
  if (!property || typeof property !== "object") return false;
  const attrs =
    property.additional_attributes && typeof property.additional_attributes === "object"
      ? property.additional_attributes
      : {};
  if (trimString(property.qma_report || attrs.qma_report)) return true;
  if (trimString(property.qma_s3_key || attrs.qma_s3_key)) return true;
  if (trimString(property.qma_report_url || attrs.qma_report_url)) return true;
  return false;
}

/**
 * True when the property has comp IDs and/or non-empty comps_analysis.
 *
 * @param {object|null|undefined} property
 * @returns {boolean}
 */
export function propertyHasComps(property) {
  if (!property || typeof property !== "object") return false;
  const attrs =
    property.additional_attributes && typeof property.additional_attributes === "object"
      ? property.additional_attributes
      : {};
  if (getAllCompIdsFromAdditionalAttributes(attrs).length > 0) return true;
  return hasCompsAnalysisContent(attrs.comps_analysis);
}

/**
 * True when the property has been distributed to investors (`is_distributed` from fetchProperties).
 *
 * @param {object|null|undefined} property
 * @returns {boolean}
 */
export function propertyIsDistributed(property) {
  if (!property || typeof property !== "object") return false;
  return Boolean(property.is_distributed);
}

/**
 * True when the property has one or more persisted photos in additional_attributes.
 *
 * @param {object|null|undefined} property
 * @returns {boolean}
 */
export function propertyHasPhotos(property) {
  if (!property || typeof property !== "object") return false;
  const attrs =
    property.additional_attributes && typeof property.additional_attributes === "object"
      ? property.additional_attributes
      : {};
  return Array.isArray(attrs.photos) && attrs.photos.length > 0;
}

/**
 * True when `square_feet`, `bedrooms`, or `bathrooms` is present and numerically zero.
 * Ignores null/undefined/"" so we do not treat missing data as zero (Number(null) and Number("") are 0 in JS).
 *
 * @param {object|null|undefined} property
 * @returns {boolean}
 */
export function propertyHasZeroSqftBedsOrBaths(property) {
  if (!property || typeof property !== "object") return false;
  const attrs =
    property.additional_attributes && typeof property.additional_attributes === "object"
      ? property.additional_attributes
      : {};

  const isExplicitZero = (value) => {
    if (value === null || value === undefined || value === "") return false;
    const n = Number(value);
    return Number.isFinite(n) && n === 0;
  };

  return (
    isExplicitZero(attrs.square_feet) ||
    isExplicitZero(attrs.bedrooms) ||
    isExplicitZero(attrs.bathrooms)
  );
}

/**
 * @param {object|null|undefined} attrs
 * @param {"bedrooms"|"bathrooms"|"square_feet"} key
 * @returns {number}
 */
export function getCoreSpecNumber(attrs, key) {
  if (!attrs || typeof attrs !== "object") return 0;
  const value = attrs[key];
  if (value === null || value === undefined || value === "") return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * True when beds, baths, or sqft is missing, invalid, or non-positive.
 *
 * @param {object|null|undefined} property
 * @returns {boolean}
 */
export function propertyMissingCoreSpecs(property) {
  if (!property || typeof property !== "object") return true;
  const attrs =
    property.additional_attributes && typeof property.additional_attributes === "object"
      ? property.additional_attributes
      : {};
  const beds = getCoreSpecNumber(attrs, "bedrooms");
  const baths = getCoreSpecNumber(attrs, "bathrooms");
  const sqft = getCoreSpecNumber(attrs, "square_feet");
  return beds <= 0 || baths <= 0 || sqft <= 0;
}

/**
 * True when beds, baths, and sqft are all finite and positive.
 *
 * @param {object|null|undefined} property
 * @returns {boolean}
 */
export function propertyCoreSpecsComplete(property) {
  return !propertyMissingCoreSpecs(property);
}

/**
 * True when ingestion likely failed: core specs missing and no Comps/QMA artifacts,
 * and the backend auto-pipeline is not still pending.
 *
 * @param {object|null|undefined} property
 * @param {{ compsPending?: boolean, qmaPending?: boolean, reportProcessing?: boolean }} [options]
 * @returns {boolean}
 */
export function shouldPromptMissingCoreSpecs(property, options = {}) {
  const { compsPending = false, qmaPending = false, reportProcessing = false } = options;
  if (compsPending || qmaPending || reportProcessing) return false;
  if (!propertyMissingCoreSpecs(property)) return false;
  if (propertyHasComps(property)) return false;
  if (propertyHasQmaReport(property)) return false;
  return true;
}

/**
 * True when saving core specs should kick off Comps and QMA (first incomplete → complete transition).
 *
 * @param {object|null|undefined} propertyBefore
 * @param {object|null|undefined} propertyAfter
 * @returns {boolean}
 */
export function shouldRunCompsQmaAfterSpecsSave(propertyBefore, propertyAfter) {
  if (!propertyMissingCoreSpecs(propertyBefore)) return false;
  if (!propertyCoreSpecsComplete(propertyAfter)) return false;
  if (propertyHasComps(propertyAfter)) return false;
  if (propertyHasQmaReport(propertyAfter)) return false;
  return true;
}

/**
 * True when the Comps→QMA pipeline can be started (matches `handleGenerateReport` preconditions).
 *
 * @param {object} [options]
 * @param {string} [options.reportStatus]
 * @param {boolean} [options.reportProcessing]
 * @param {boolean} [options.compsPending]
 * @param {boolean} [options.qmaPending]
 * @param {string|null|undefined} [options.propertyId]
 * @param {string|null|undefined} [options.userId]
 * @param {string|null|undefined} [options.customerId]
 * @returns {boolean}
 */
export function canTriggerCompsQmaPipeline(options = {}) {
  const {
    reportStatus = REPORT_STATUS_INITIAL,
    reportProcessing = false,
    compsPending = false,
    qmaPending = false,
    propertyId,
    userId,
    customerId,
  } = options;

  if (reportProcessing || compsPending || qmaPending) return false;
  if (reportStatus !== REPORT_STATUS_INITIAL) return false;

  const hasId = (value) => typeof value === "string" && value.trim().length > 0;
  return hasId(propertyId) && hasId(userId) && hasId(customerId);
}
